import React, { useCallback, useEffect, useState } from "react";
import { FaWallet, FaUsers, FaCog, FaHistory, FaBolt, FaSearch, FaFilter, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import {
    getFreeCashSettings,
    updateFreeCashSettings,
    getFreeCashSegmentPreview,
    loadFreeCashSegment,
    loadFreeCashCustom,
    getFreeCashFilteredCustomers,
    getFreeCashLoadHistory,
} from "../../services/ClientService";
import ConfirmModal from "../ConfirmModal";
import FreeCashExpiryFields from "./FreeCashExpiryFields";
import { buildFreeCashLoadPayload } from "../../utils/orderBillingHelpers";

const TABS = [
    { id: "segment", label: "Load Free Cash", icon: <FaBolt /> },
    { id: "custom", label: "Custom Free Cash", icon: <FaUsers /> },
    { id: "auto", label: "Auto After Order", icon: <FaCog /> },
    { id: "history", label: "History", icon: <FaHistory /> },
];

const defaultExpiry = { days: 7, hours: 0, minutes: 0 };

const FreeCash = () => {
    const [activeTab, setActiveTab] = useState("segment");
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ show: false, message: "", type: "success" });
    const [confirmModal, setConfirmModal] = useState({ visible: false, message: "", onConfirm: null });

    const showToast = (message, type = "success") => {
        setSnackbar({ show: true, message, type });
        setTimeout(() => setSnackbar({ show: false, message: "", type: "success" }), 4000);
    };

    const [segmentForm, setSegmentForm] = useState({
        orderCondition: "first_order",
        amount: "",
        minCartValue: "",
        ...defaultExpiry,
    });
    const [segmentCount, setSegmentCount] = useState(null);

    const [customForm, setCustomForm] = useState({
        amount: "",
        minCartValue: "",
        ...defaultExpiry,
    });
    const [customers, setCustomers] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [allFilteredIds, setAllFilteredIds] = useState([]);
    const [customersLoading, setCustomersLoading] = useState(false);
    const [selectAllLoading, setSelectAllLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 0 });
    const [filters, setFilters] = useState({
        searchTerm: "",
        minOrders: "",
        maxOrders: "",
        minRevenue: "",
        maxRevenue: "",
        minWalletBalance: "",
        maxWalletBalance: "",
        lastOrderFrom: "",
        lastOrderTo: "",
        gender: "",
        hasOrders: "",
    });

    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    const loadSettings = useCallback(async () => {
        const res = await getFreeCashSettings();
        if (res?.success) setSettings(res.data || {});
    }, []);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const previewSegment = async () => {
        const res = await getFreeCashSegmentPreview({ orderCondition: segmentForm.orderCondition });
        if (res?.success) setSegmentCount(res.count);
    };

    useEffect(() => {
        if (activeTab === "segment") previewSegment();
    }, [activeTab, segmentForm.orderCondition]);

    const buildFilterPayload = useCallback((extra = {}) => {
        const merged = { ...filters, ...extra };
        const payload = { ...extra };
        if (merged.searchTerm?.trim()) payload.searchTerm = merged.searchTerm.trim();
        if (merged.minOrders !== "" && merged.minOrders !== undefined) payload.minOrders = Number(merged.minOrders);
        if (merged.maxOrders !== "" && merged.maxOrders !== undefined) payload.maxOrders = Number(merged.maxOrders);
        if (merged.minRevenue !== "" && merged.minRevenue !== undefined) payload.minRevenue = Number(merged.minRevenue);
        if (merged.maxRevenue !== "" && merged.maxRevenue !== undefined) payload.maxRevenue = Number(merged.maxRevenue);
        if (merged.minWalletBalance !== "" && merged.minWalletBalance !== undefined) payload.minWalletBalance = Number(merged.minWalletBalance);
        if (merged.maxWalletBalance !== "" && merged.maxWalletBalance !== undefined) payload.maxWalletBalance = Number(merged.maxWalletBalance);
        if (merged.lastOrderFrom) payload.lastOrderFrom = merged.lastOrderFrom;
        if (merged.lastOrderTo) payload.lastOrderTo = merged.lastOrderTo;
        if (merged.gender) payload.gender = merged.gender;
        if (merged.hasOrders !== "" && merged.hasOrders !== undefined) payload.hasOrders = merged.hasOrders === "true";
        return payload;
    }, [filters]);

    const fetchCustomers = useCallback(async (page = 1, override = {}) => {
        setCustomersLoading(true);
        try {
            const payload = buildFilterPayload({ page, limit: pagination.limit, ...override });
            const res = await getFreeCashFilteredCustomers(payload);
            if (res?.success) {
                setCustomers(res.data || []);
                setPagination((prev) => ({ ...prev, page, ...(res.pagination || {}) }));
            }
        } catch (error) {
            console.error("[FreeCash] fetchCustomers error:", error);
            showToast("Failed to load customers", "error");
        } finally {
            setCustomersLoading(false);
        }
    }, [buildFilterPayload, pagination.limit]);

    const fetchAllFilteredIds = useCallback(async () => {
        try {
            const payload = buildFilterPayload({ selectAll: true });
            const res = await getFreeCashFilteredCustomers(payload);
            if (res?.success) {
                setAllFilteredIds((res.data || []).map((id) => (id?.toString ? id.toString() : String(id))));
            }
        } catch (error) {
            console.error("[FreeCash] fetchAllFilteredIds error:", error);
        }
    }, [buildFilterPayload]);

    useEffect(() => {
        if (activeTab === "custom") fetchCustomers(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    useEffect(() => {
        if (selectAll && allFilteredIds.length > 0) {
            setSelectedIds(allFilteredIds);
        }
    }, [selectAll, allFilteredIds]);

    const effectiveSelectedCount = selectAll ? pagination.total : selectedIds.length;

    const handleSearchChange = (value) => {
        setFilters((prev) => ({ ...prev, searchTerm: value }));
        setSelectAll(false);
        if (searchTimeout) clearTimeout(searchTimeout);
        setSearchTimeout(setTimeout(() => fetchCustomers(1, { searchTerm: value }), 500));
    };

    const handleApplyFilters = () => {
        setSelectedIds([]);
        setSelectAll(false);
        fetchCustomers(1);
    };

    const handleClearFilters = async () => {
        setFilters({
            searchTerm: "",
            minOrders: "",
            maxOrders: "",
            minRevenue: "",
            maxRevenue: "",
            minWalletBalance: "",
            maxWalletBalance: "",
            lastOrderFrom: "",
            lastOrderTo: "",
            gender: "",
            hasOrders: "",
        });
        setSelectedIds([]);
        setSelectAll(false);
        setAllFilteredIds([]);
        setCustomersLoading(true);
        try {
            const res = await getFreeCashFilteredCustomers({ page: 1, limit: pagination.limit });
            if (res?.success) {
                setCustomers(res.data || []);
                setPagination((prev) => ({ ...prev, page: 1, ...(res.pagination || {}) }));
            }
        } catch (error) {
            console.error("[FreeCash] handleClearFilters error:", error);
            showToast("Failed to load customers", "error");
        } finally {
            setCustomersLoading(false);
        }
    };

    const toggleSelectCustomer = (customerId) => {
        const id = customerId?.toString?.() || String(customerId);
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
        setSelectAll(false);
    };

    const handleSelectAll = async () => {
        if (selectAll) {
            setSelectedIds([]);
            setSelectAll(false);
            setAllFilteredIds([]);
        } else {
            setSelectAllLoading(true);
            try {
                await fetchAllFilteredIds();
                setSelectAll(true);
            } finally {
                setSelectAllLoading(false);
            }
        }
    };

    const formatLastOrderDate = (value) => {
        if (!value) return null;
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return null;
        const day = String(d.getDate()).padStart(2, "0");
        const month = d.toLocaleString("en-US", { month: "short" });
        const year = d.getFullYear();
        const hours24 = d.getHours();
        const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
        const hh = String(hours12).padStart(2, "0");
        const mm = String(d.getMinutes()).padStart(2, "0");
        const ampm = hours24 >= 12 ? "PM" : "AM";
        return `${day} ${month} ${year} ${hh}:${mm} ${ampm}`;
    };

    const fetchHistory = async () => {
        setHistoryLoading(true);
        try {
            const res = await getFreeCashLoadHistory({ page: 1, limit: 30 });
            if (res?.success) setHistory(res.data || []);
        } finally {
            setHistoryLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === "history") fetchHistory();
    }, [activeTab]);

    const confirmLoadFreeCash = ({ form, message, onSuccess, extra = {} }) => {
        if (!form.amount || Number(form.amount) <= 0) {
            showToast("Enter a valid amount", "error");
            return;
        }
        setConfirmModal({
            visible: true,
            message,
            onConfirm: async () => {
                setConfirmModal({ visible: false, message: "", onConfirm: null });
                setLoading(true);
                try {
                    const payload = buildFreeCashLoadPayload(form, extra);
                    const res = extra.userIds
                        ? await loadFreeCashCustom(payload)
                        : await loadFreeCashSegment(payload);
                    showToast(res?.message || "Loaded", res?.success ? "success" : "error");
                    if (res?.success) onSuccess?.();
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handleLoadSegment = () => {
        confirmLoadFreeCash({
            form: segmentForm,
            message: `Load ₹${segmentForm.amount} Free Cash to ${segmentCount ?? "matching"} customers?`,
            extra: { orderCondition: segmentForm.orderCondition },
            onSuccess: previewSegment,
        });
    };

    const handleLoadCustom = () => {
        if (effectiveSelectedCount === 0) {
            showToast("Select at least one customer", "error");
            return;
        }
        confirmLoadFreeCash({
            form: customForm,
            message: `Load ₹${customForm.amount} Free Cash to ${effectiveSelectedCount} selected customer${effectiveSelectedCount > 1 ? "s" : ""}?`,
            extra: { userIds: selectAll && allFilteredIds.length > 0 ? allFilteredIds : selectedIds },
            onSuccess: () => {
                setSelectedIds([]);
                setSelectAll(false);
                fetchCustomers(pagination.page);
            },
        });
    };

    const saveAutoSettings = async () => {
        setLoading(true);
        try {
            const res = await updateFreeCashSettings(settings || {});
            showToast(res?.message || "Settings saved", res?.success ? "success" : "error");
            if (res?.success) setSettings(res.data);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6">
            <div className="max-w-7xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 rounded-2xl bg-teal-100 text-teal-700">
                        <FaWallet size={24} />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Free Cash</h1>
                        <p className="text-gray-500 text-sm">Client-funded wallet credits for customers — no customer top-up</p>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                    {TABS.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveTab(tab.id)}
                            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border ${
                                activeTab === tab.id
                                    ? "bg-teal-600 text-white border-teal-600"
                                    : "bg-white text-gray-700 border-gray-200"
                            }`}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}
                </div>

                {activeTab === "segment" && (
                    <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm space-y-4">
                        <h2 className="text-lg font-bold text-gray-900">Load by order milestone</h2>
                        <p className="text-sm text-gray-500">Counts delivered online orders only.</p>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium text-gray-700">Customer segment</label>
                                <select
                                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2"
                                    value={segmentForm.orderCondition}
                                    onChange={(e) => setSegmentForm({ ...segmentForm, orderCondition: e.target.value })}
                                >
                                    <option value="first_order">First order (0 delivered)</option>
                                    <option value="second_order">Second order (1 delivered)</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Matching customers</label>
                                <div className="mt-1 rounded-lg bg-teal-50 text-teal-800 px-3 py-2 font-semibold">
                                    {segmentCount ?? "—"} customers
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Amount (₹)</label>
                                <input
                                    type="number"
                                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2"
                                    value={segmentForm.amount}
                                    onChange={(e) => setSegmentForm({ ...segmentForm, amount: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Min cart value (₹)</label>
                                <input
                                    type="number"
                                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2"
                                    value={segmentForm.minCartValue}
                                    onChange={(e) => setSegmentForm({ ...segmentForm, minCartValue: e.target.value })}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-2 block">Expiry</label>
                            <FreeCashExpiryFields value={segmentForm} onChange={setSegmentForm} />
                        </div>
                        <button
                            type="button"
                            disabled={loading}
                            onClick={handleLoadSegment}
                            className="px-6 py-2.5 rounded-xl bg-teal-600 text-white font-semibold disabled:opacity-50"
                        >
                            Load Free Cash
                        </button>
                    </div>
                )}

                {activeTab === "custom" && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 space-y-4">
                            <div className="bg-white rounded-xl border border-gray-200 p-4">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="flex-1 relative">
                                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            value={filters.searchTerm}
                                            onChange={(e) => handleSearchChange(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                                            placeholder="Search by name, email, or mobile..."
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setShowFilters(!showFilters)}
                                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                            showFilters ? "bg-teal-100 text-teal-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                        }`}
                                    >
                                        <FaFilter /> Filters {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                                    </button>
                                </div>

                                <AnimatePresence>
                                    {showFilters && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="pt-3 border-t border-gray-200 space-y-3">
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1">Min Orders</label>
                                                        <input type="number" min="0" value={filters.minOrders} onChange={(e) => setFilters((prev) => ({ ...prev, minOrders: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1">Max Orders</label>
                                                        <input type="number" min="0" value={filters.maxOrders} onChange={(e) => setFilters((prev) => ({ ...prev, maxOrders: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="100" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1">Min Revenue (₹)</label>
                                                        <input type="number" min="0" value={filters.minRevenue} onChange={(e) => setFilters((prev) => ({ ...prev, minRevenue: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1">Max Revenue (₹)</label>
                                                        <input type="number" min="0" value={filters.maxRevenue} onChange={(e) => setFilters((prev) => ({ ...prev, maxRevenue: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="50000" />
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1">Last Order From</label>
                                                        <input type="date" value={filters.lastOrderFrom} onChange={(e) => setFilters((prev) => ({ ...prev, lastOrderFrom: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1">Last Order To</label>
                                                        <input type="date" value={filters.lastOrderTo} onChange={(e) => setFilters((prev) => ({ ...prev, lastOrderTo: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1">Gender</label>
                                                        <select value={filters.gender} onChange={(e) => setFilters((prev) => ({ ...prev, gender: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                                                            <option value="">All</option>
                                                            <option value="Male">Male</option>
                                                            <option value="Female">Female</option>
                                                            <option value="Other">Other</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1">Has Orders</label>
                                                        <select value={filters.hasOrders} onChange={(e) => setFilters((prev) => ({ ...prev, hasOrders: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                                                            <option value="">All</option>
                                                            <option value="true">With Orders</option>
                                                            <option value="false">No Orders</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1">Min Wallet (₹)</label>
                                                        <input type="number" min="0" value={filters.minWalletBalance} onChange={(e) => setFilters((prev) => ({ ...prev, minWalletBalance: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="0" />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 mb-1">Max Wallet (₹)</label>
                                                        <input type="number" min="0" value={filters.maxWalletBalance} onChange={(e) => setFilters((prev) => ({ ...prev, maxWalletBalance: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="500" />
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button type="button" onClick={handleApplyFilters} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700">
                                                        Apply Filters
                                                    </button>
                                                    <button type="button" onClick={handleClearFilters} className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200">
                                                        Clear
                                                    </button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={selectAll}
                                            onChange={handleSelectAll}
                                            disabled={selectAllLoading || customersLoading}
                                            className="w-4 h-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500 disabled:opacity-50"
                                        />
                                        <span className="text-gray-600">
                                            Select All ({pagination.total}){selectAllLoading ? " — loading..." : ""}
                                        </span>
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <FaUsers className="text-teal-600" />
                                        <span className="text-sm font-medium text-teal-700">{effectiveSelectedCount} selected</span>
                                    </div>
                                </div>

                                {customersLoading ? (
                                    <div className="flex items-center justify-center py-12">
                                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-600" />
                                    </div>
                                ) : customers.length === 0 ? (
                                    <div className="text-center py-12">
                                        <FaUsers className="mx-auto text-2xl text-gray-300 mb-2" />
                                        <p className="text-gray-500 text-sm">No customers found</p>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-gray-100 max-h-[420px] overflow-y-auto">
                                        {customers.map((customer) => {
                                            const cid = customer._id?.toString?.() || String(customer._id);
                                            const isSelected = selectedIds.includes(cid);
                                            const lastOrderLabel = formatLastOrderDate(customer.lastOrderDate);
                                            return (
                                                <div
                                                    key={cid}
                                                    onClick={() => toggleSelectCustomer(cid)}
                                                    className={`flex items-center gap-4 px-4 py-3 cursor-pointer transition-colors ${
                                                        isSelected ? "bg-teal-50" : "hover:bg-gray-50"
                                                    }`}
                                                >
                                                    <input type="checkbox" checked={isSelected} readOnly className="w-4 h-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500" />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-medium text-gray-800 truncate">{customer.name || "Unknown"}</span>
                                                            {customer.gender && <span className="text-xs text-gray-400">{customer.gender}</span>}
                                                        </div>
                                                        <div className="text-xs text-gray-400 truncate mt-0.5">{customer.mobile || customer.email || "-"}</div>
                                                    </div>
                                                    <div className="text-right shrink-0">
                                                        <div className="text-xs font-semibold text-teal-700">Wallet ₹{(customer.freeCashBalance || 0).toLocaleString("en-IN")}</div>
                                                        <div className="text-xs text-gray-500">{customer.orderCount || 0} orders</div>
                                                        <div className="text-xs text-green-600 font-medium">₹{(customer.totalRevenue || 0).toLocaleString("en-IN")}</div>
                                                        {lastOrderLabel && <div className="text-[10px] text-gray-400 mt-0.5">Last: {lastOrderLabel}</div>}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {pagination.totalPages > 0 && (
                                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                                        <span className="text-xs text-gray-500">
                                            Page {pagination.page} of {pagination.totalPages} ({pagination.total} customers)
                                        </span>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => fetchCustomers(pagination.page - 1)}
                                                disabled={pagination.page <= 1 || customersLoading}
                                                className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-100"
                                            >
                                                Previous
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => fetchCustomers(pagination.page + 1)}
                                                disabled={pagination.page >= pagination.totalPages || customersLoading}
                                                className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-100"
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-3 h-fit">
                            <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                                <FaWallet className="text-teal-600" /> Load Free Cash
                            </h3>
                            <p className="text-xs text-gray-500">Credit selected customers — client-funded only.</p>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
                                <input type="number" className="w-full rounded-lg border border-gray-200 px-3 py-2" value={customForm.amount} onChange={(e) => setCustomForm({ ...customForm, amount: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Min cart value (₹)</label>
                                <input type="number" className="w-full rounded-lg border border-gray-200 px-3 py-2" value={customForm.minCartValue} onChange={(e) => setCustomForm({ ...customForm, minCartValue: e.target.value })} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Expiry</label>
                                <FreeCashExpiryFields value={customForm} onChange={setCustomForm} />
                            </div>
                            <button
                                type="button"
                                disabled={loading || customersLoading || selectAllLoading || (selectAll && allFilteredIds.length === 0)}
                                onClick={handleLoadCustom}
                                className="w-full py-2.5 rounded-xl bg-teal-600 text-white font-semibold disabled:opacity-50 hover:bg-teal-700"
                            >
                                Load to {effectiveSelectedCount} Customer{effectiveSelectedCount === 1 ? "" : "s"}
                            </button>
                        </div>
                    </div>
                )}

                {activeTab === "auto" && settings && (
                    <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm space-y-4">
                        <label className="flex items-center gap-3">
                            <input
                                type="checkbox"
                                checked={!!settings.autoFreeCashEnabled}
                                onChange={(e) => setSettings({ ...settings, autoFreeCashEnabled: e.target.checked })}
                            />
                            <span className="font-semibold text-gray-900">Auto Free Cash after order</span>
                        </label>
                        <div className="grid md:grid-cols-2 gap-4">
                            {[
                                ["globalMaxActiveBalance", "Global max active wallet (₹)"],
                                ["maxFreeCashPerOrder", "Max Free Cash per order (₹)"],
                                ["percentPerOrder", "Percent per order (%)"],
                                ["decrementPercentPerOrder", "Decrement % per order"],
                                ["finalDecrementPercent", "Final decrement % floor"],
                                ["autoMinCartValue", "Auto reward min cart (₹)"],
                                ["maxAutoFreeCashOrderCount", "Max count of auto Free Cash (0 = unlimited)"],
                            ].map(([key, label]) => (
                                <div key={key}>
                                    <label className="text-sm font-medium text-gray-700">{label}</label>
                                    <input
                                        type="number"
                                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2"
                                        value={settings[key] ?? 0}
                                        onChange={(e) => setSettings({ ...settings, [key]: Number(e.target.value) })}
                                    />
                                </div>
                            ))}
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-2 block">Auto credit expiry</label>
                            <FreeCashExpiryFields
                                value={{
                                    days: settings.autoExpiryDays,
                                    hours: settings.autoExpiryHours,
                                    minutes: settings.autoExpiryMinutes,
                                }}
                                onChange={(v) =>
                                    setSettings({
                                        ...settings,
                                        autoExpiryDays: Number(v.days || 0),
                                        autoExpiryHours: Number(v.hours || 0),
                                        autoExpiryMinutes: Number(v.minutes || 0),
                                    })
                                }
                            />
                        </div>
                        <button
                            type="button"
                            disabled={loading}
                            onClick={saveAutoSettings}
                            className="px-6 py-2.5 rounded-xl bg-teal-600 text-white font-semibold disabled:opacity-50"
                        >
                            Save settings
                        </button>
                    </div>
                )}

                {activeTab === "history" && (
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                        {historyLoading ? (
                            <div className="p-8 text-center text-gray-500">Loading...</div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 text-left text-gray-600">
                                    <tr>
                                        <th className="px-4 py-3">Date</th>
                                        <th className="px-4 py-3">Source</th>
                                        <th className="px-4 py-3">Amount</th>
                                        <th className="px-4 py-3">Customers</th>
                                        <th className="px-4 py-3">Total</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {history.map((row) => (
                                        <tr key={row.batchId || row._id}>
                                            <td className="px-4 py-3">{row.createdAt ? new Date(row.createdAt).toLocaleString() : "—"}</td>
                                            <td className="px-4 py-3">{row.source}</td>
                                            <td className="px-4 py-3">₹{row.amount}</td>
                                            <td className="px-4 py-3">{row.customerCount}</td>
                                            <td className="px-4 py-3 font-semibold">₹{row.totalCredited}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>

            {snackbar.show && (
                <div
                    className={`fixed bottom-6 right-6 px-4 py-3 rounded-xl text-white shadow-lg ${
                        snackbar.type === "error" ? "bg-red-600" : "bg-teal-600"
                    }`}
                >
                    {snackbar.message}
                </div>
            )}

            <ConfirmModal
                visible={confirmModal.visible}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal({ visible: false, message: "", onConfirm: null })}
            />
        </div>
    );
};

export default FreeCash;
